"use client";

import { X } from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { clearStoredCoursePreviewToken } from "@/lib/course-preview";
import { setCoursePreviewFetcherToken } from "@/lib/api";
import type { Locale } from "@/lib/i18n";

const COPY = {
  en: {
    line1: "Preview mode, changes are not saved, progress is not tracked.",
    exit: "Exit preview",
  },
  bn: {
    line1: "প্রিভিউ মোড, পরিবর্তন সংরক্ষিত হয় না, অগ্রগতি ট্র্যাক হয় না।",
    exit: "প্রিভিউ বন্ধ করুন",
  },
} as const;

type Props = {
  locale: Locale;
  messageEn?: string;
  messageBn?: string;
};

export function CoursePreviewBanner({ locale, messageEn, messageBn }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const t = COPY[locale] ?? COPY.en;

  function exitPreview() {
    clearStoredCoursePreviewToken();
    setCoursePreviewFetcherToken(null);
    void queryClient.invalidateQueries();
    router.replace(pathname);
    router.refresh();
  }

  return (
    <div
      role="status"
      className="sticky top-0 z-[60] flex w-full items-start gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm sm:items-center sm:justify-between"
    >
      <div className="min-w-0 flex-1 space-y-1 text-sm leading-snug">
        <p className="font-medium">{messageEn ?? COPY.en.line1}</p>
        <p className="text-xs text-amber-900/90 sm:text-sm">{messageBn ?? COPY.bn.line1}</p>
      </div>
      <Button
        type="button"
        variant="secondary"
        className="h-9 shrink-0 gap-1 border-amber-300 bg-white px-3 text-sm text-amber-950 hover:bg-amber-100"
        onClick={exitPreview}
      >
        <X className="h-4 w-4" />
        {t.exit}
      </Button>
    </div>
  );
}
